import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket, { SOCKET_RECOVER_EVENT } from "../../services/socket-io";
import clsx from "clsx";

import { Paper, makeStyles } from "@material-ui/core";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import TicketResponseWindow from "../TicketResponseWindow";
import MessagesList from "../MessagesList";
import TicketChatFlowStatus from "../TicketChatFlowStatus";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
  },
  responseWindowBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "4px 12px",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    backgroundColor: "rgba(0,0,0,0.02)",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [pixelTracking, setPixelTracking] = useState(null);

  useEffect(() => {
    setLoading(true);
    setPixelTracking(null);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);

          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, history]);

  useEffect(() => {
    if (!ticketId) {
      return undefined;
    }

    let cancelled = false;
    const fetchPixelTracking = async () => {
      try {
        const { data } = await api.get(`/pixel/ticket/${ticketId}`);
        if (!cancelled) {
          setPixelTracking(data.pixelTracking || null);
        }
      } catch (err) {
        if (!cancelled) {
          setPixelTracking(null);
        }
      }
    };

    fetchPixelTracking();
    return () => {
      cancelled = true;
    };
  }, [ticketId]);

  useEffect(() => {
    const socket = openSocket();
    if (!socket) return undefined;

    const currentTicketId = String(ticketId);

    const handleTicket = (data) => {
      if (String(data.ticket?.id) !== currentTicketId) {
        return;
      }

      if (data.action === "update") {
        setTicket(data.ticket);
      }

      if (data.action === "delete") {
        toast.success("Ticket deleted sucessfully.");
        history.push("/tickets");
      }
    };

    const handleContact = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    };

    const handlePixelTracking = (data) => {
      if (data.action !== "update") {
        return;
      }
      const trackingTicketId = String(
        data.pixelTracking?.ticketId || data.ticket?.id || ""
      );
      if (trackingTicketId !== currentTicketId) {
        return;
      }
      if (data.pixelTracking) {
        setPixelTracking(data.pixelTracking);
      }
    };

    const handleConnect = () => {
      socket.emit("joinChatBox", currentTicketId);
    };

    const handleRecover = () => {
      handleConnect();
    };

    socket.on("connect", handleConnect);
    socket.on("ticket", handleTicket);
    socket.on("contact", handleContact);
    socket.on("pixelTracking", handlePixelTracking);
    window.addEventListener(SOCKET_RECOVER_EVENT, handleRecover);

    if (socket.connected) {
      handleConnect();
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("ticket", handleTicket);
      socket.off("contact", handleContact);
      socket.off("pixelTracking", handlePixelTracking);
      window.removeEventListener(SOCKET_RECOVER_EVENT, handleRecover);
    };
  }, [ticketId, history]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              pixelTracking={pixelTracking}
              onClick={handleDrawerOpen}
              action={<TicketActionButtons ticket={ticket} />}
            />
          </div>
        </TicketHeader>
        {!loading && ticketId ? (
          <div className={classes.responseWindowBar}>
            <TicketResponseWindow ticketId={ticketId} />
          </div>
        ) : null}
        <ReplyMessageProvider>
          <MessagesList
            ticketId={ticketId}
            isGroup={ticket.isGroup}
          ></MessagesList>
          {!loading && ticketId ? (
            <TicketChatFlowStatus ticketId={ticketId} />
          ) : null}
          <MessageInput ticketStatus={ticket.status} ticket={ticket} />
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
        onTicketUpdate={setTicket}
      />
    </div>
  );
};

export default Ticket;
